import { apiService } from './api'
import { DashboardStats, AdminDashboardStats, Activity } from '../types/dashboard'
import { DashboardStats as RiskStats } from '../components/dashboard/RiskOverview'
import { MonthlyTrendData } from '../components/dashboard/MonthlyTrendChart'

export class DashboardService {
  // 個人ダッシュボード統計
  async getUserDashboardStats(): Promise<DashboardStats> {
    try {
      // 個人の受講一覧を取得
      const enrollments = await apiService.get('/enrollments/my')
      return this.calculateUserStats(enrollments)
    } catch (error: any) {
      console.warn('APIが利用できません。デモデータを返します。', error.message)

      // 429エラー（レート制限）やその他のAPIエラーの場合はデモデータを返す
      if (error.response?.status === 429 || error.response?.status >= 400) {
        console.log('🎭 API エラーのためデモモードに切り替え')
      }

      return this.getDemoUserStats()
    }
  }

  private calculateUserStats(enrollments: any): DashboardStats {
    // 統計データを計算
    const totalEnrollments = enrollments.enrollments?.length || 0
    const completedEnrollments =
      enrollments.enrollments?.filter((e: any) => e.status === 'COMPLETED').length || 0
    const pendingEnrollments =
      enrollments.enrollments?.filter((e: any) => e.status === 'ASSIGNED').length || 0
    const overdueEnrollments = enrollments.enrollments?.filter((e: any) => e.isOverdue).length || 0

    // 最近のテスト結果（模擬データ - 実際はAPIから取得）
    const recentTestResults =
      enrollments.enrollments
        ?.filter((e: any) => e.latestResult)
        .map((e: any) => ({
          id: e.latestResult.id,
          score: e.latestResult.score,
          passed: e.latestResult.passed,
          completedAt: e.latestResult.completedAt,
          test: {
            title: e.test.title,
            complianceRule: {
              category: e.test.complianceRule.category,
            },
          },
        }))
        .slice(0, 5) || []

    // 平均スコア計算
    const scores = recentTestResults.map((r: any) => r.score as number)
    const averageScore =
      scores.length > 0
        ? Math.round(scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length)
        : 0

    // 期限の近い受講
    const upcomingDeadlines =
      enrollments.enrollments
        ?.filter((e: any) => e.dueDate && e.status !== 'COMPLETED')
        .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
        .slice(0, 5) || []

    return {
      totalEnrollments,
      completedEnrollments,
      pendingEnrollments,
      overdueEnrollments,
      averageScore,
      recentTestResults,
      upcomingDeadlines,
    }
  }

  private getDemoUserStats(): DashboardStats {
    return {
      totalEnrollments: 8,
      completedEnrollments: 5,
      pendingEnrollments: 2,
      overdueEnrollments: 1,
      averageScore: 85,
      recentTestResults: [
        {
          id: 'demo-1',
          score: 92,
          passed: true,
          completedAt: new Date().toISOString(),
          test: {
            title: 'ハラスメント防止ガイドライン',
            complianceRule: { category: 'ハラスメント防止' },
          },
        },
        {
          id: 'demo-2',
          score: 78,
          passed: false,
          completedAt: new Date(Date.now() - 86400000).toISOString(),
          test: {
            title: '情報セキュリティ基礎',
            complianceRule: { category: '情報セキュリティ' },
          },
        },
      ],
      upcomingDeadlines: [
        {
          id: 'demo-deadline-1',
          test: {
            title: 'コンプライアンス総合テスト',
            complianceRule: { category: 'コンプライアンス基礎' },
          },
          dueDate: new Date(Date.now() + 7 * 86400000).toISOString(),
          status: 'ASSIGNED',
        },
      ],
    }
  }

  // リスク概要統計を取得
  async getRiskStats(): Promise<RiskStats> {
    try {
      const response = await apiService.get<RiskStats>('/dashboard/stats')
      return response
    } catch (error) {
      console.warn('Dashboard stats API unavailable, returning demo data')
      return {
        completionRate: 0,
        averageScore: 0,
        pendingCount: 0,
        totalEnrollments: 0,
        completedCount: 0,
      }
    }
  }

  // 月別トレンドデータを取得
  async getMonthlyTrend(): Promise<MonthlyTrendData[]> {
    try {
      const response = await apiService.get<{ trend: MonthlyTrendData[] }>(
        '/dashboard/monthly-trend'
      )
      return response.trend || []
    } catch (error) {
      console.warn('Monthly trend API unavailable')
      return []
    }
  }

  // 最近のアクティビティを取得
  async getRecentActivity(limit: number = 5): Promise<Activity[]> {
    try {
      const response = await apiService.get<{ activities: Activity[] }>(
        `/dashboard/recent-activity?limit=${limit}`
      )
      return response.activities || []
    } catch (error) {
      console.warn('Recent activity API unavailable, returning empty list')
      return []
    }
  }

  // 管理者ダッシュボード統計
  async getAdminDashboardStats(): Promise<AdminDashboardStats> {
    try {
      const [userStats, complianceStats, enrollmentStats, recentActivity] = await Promise.all([
        apiService.get('/users/stats/overview'),
        apiService.get('/compliance/stats'),
        apiService.get('/enrollments/stats'),
        this.getRecentActivity(5),
      ])

      const userStatsData = userStats as any
      const complianceStatsData = complianceStats as any
      const enrollmentStatsData = enrollmentStats as any

      // 完了率計算
      const completionRate =
        enrollmentStatsData.totalEnrollments > 0
          ? Math.round(
              ((enrollmentStatsData.statusDistribution.find((s: any) => s.status === 'COMPLETED')
                ?.count || 0) /
                enrollmentStatsData.totalEnrollments) *
                100
            )
          : 0

      // カテゴリ別統計（模擬データ）
      const categoryStats =
        complianceStatsData.categoryStats?.map((cat: any) => ({
          category: cat.category,
          totalTests: cat.count,
          completionRate: Math.floor(Math.random() * 30) + 70, // 70-100%の範囲
          averageScore: Math.floor(Math.random() * 20) + 75, // 75-95の範囲
        })) || []

      return {
        totalUsers: userStatsData.totalUsers || 0,
        totalRules: complianceStatsData.totalRules || 0,
        totalTests: complianceStatsData.totalTests || 0,
        activeEnrollments: enrollmentStatsData.totalEnrollments || 0,
        completionRate,
        categoryStats,
        recentActivity,
      }
    } catch (error) {
      console.warn('APIが利用できません。デモデータを返します。')
      return this.getDemoAdminStats()
    }
  }

  // システム概要統計（全ユーザー向け）
  async getSystemOverview() {
    try {
      const [complianceStats] = await Promise.all([apiService.get('/compliance/stats')])

      const stats = complianceStats as any
      return {
        totalRules: stats.totalRules || 0,
        totalTests: stats.totalTests || 0,
        categories: stats.categoryStats || [],
      }
    } catch (error) {
      console.error('System overview error:', error)
      return {
        totalRules: 0,
        totalTests: 0,
        categories: [],
      }
    }
  }

  private getDemoAdminStats(): AdminDashboardStats {
    return {
      totalUsers: 156,
      totalRules: 23,
      totalTests: 45,
      activeEnrollments: 89,
      completionRate: 78,
      categoryStats: [
        {
          category: 'ハラスメント防止',
          totalTests: 8,
          completionRate: 85,
          averageScore: 88,
        },
        {
          category: '情報セキュリティ',
          totalTests: 12,
          completionRate: 72,
          averageScore: 82,
        },
        {
          category: 'コンプライアンス基礎',
          totalTests: 15,
          completionRate: 90,
          averageScore: 86,
        },
      ],
      recentActivity: [
        {
          id: 'demo-activity-1',
          type: 'test_completed',
          timestamp: new Date().toISOString(),
          user: { name: 'Taro Tanaka' },
          testTitle: 'Harassment Prevention',
          score: 92,
        },
        {
          id: 'demo-activity-2',
          type: 'enrollment_created',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          testTitle: 'Data Protection Standards',
          count: 15,
        },
      ],
    }
  }
}

export const dashboardService = new DashboardService()
